import i18n from 'i18next';

/**
 * Универсальный «парсер» ошибок axios → диспатч в стор.
 *
 * Контракт `errorAction` — по числу параметров:
 *   - `(errorMessage)` — получаешь строку с уже извлечённым текстом.
 *   - `(errorMessage, isNetworkError, statusCode)` — позиционно, для
 *     слайсов, которые хранят флаг сети / код статуса отдельными полями.
 *
 * Раньше при `length > 1` вызов делался одним объектом
 * (`errorAction(errorData)`), и слайсы вида
 * `(errorMessage, isNetworkError) => dispatch(setError({errorMessage, isNetworkError}))`
 * ловили в `errorMessage` весь объект (`{errorMessage, isNetworkError, statusCode}`)
 * и записывали его в `state.error`. Затем UI пытался отрендерить объект
 * как ReactNode → «Objects are not valid as a React child».
 *
 * Текст уходит на экран как есть, поэтому он всегда на языке сайта: свои тексты
 * ответа (`errors`, `error`, `message`) переводит бэкенд, а без них — ключ `errors.*`
 * по коду ответа. Английские тексты axios («Request failed with status code 500»,
 * «Network Error») и JS-исключений пользователю не показываются (168-ФЗ).
 */

/**
 * Тексты Laravel по умолчанию — бэкенд их не переводит: PreventRequestForgery (419),
 * AuthenticationException (401), AuthorizationException (403), ThrottleRequests (429),
 * Handler без debug (500), режим обслуживания `artisan down` (503).
 */
const LARAVEL_DEFAULT_MESSAGES = [
    'CSRF token mismatch.',
    'Unauthenticated.',
    'This action is unauthorized.',
    'Too Many Attempts.',
    'Server Error',
    'Service Unavailable',
];

function messageByStatus(statusCode) {
    if (statusCode === 401) {
        return i18n.t('errors.unauthenticated');
    }
    if (statusCode === 419) {
        return i18n.t('errors.page_expired');
    }
    if (statusCode === 429) {
        return i18n.t('errors.too_many_requests');
    }
    if (statusCode >= 500) {
        return i18n.t('errors.server');
    }

    return i18n.t('errors.generic');
}

export function handleResponseError(error, errorAction) {
    let errors;
    let message = '';
    let isNetworkError = false; // Флаг для сетевой ошибки
    let statusCode = error.response?.status || null; // Код ответа сервера

    if (error.response) {
        errors = error.response.data?.errors;
        const serverMessage = error.response.data?.message;

        if (errors) {
            message = Object.values(errors).flat().join('\n');
        } else if (error.response.data?.error) { // 🛠 Берём `error` из ответа сервера
            message = error.response.data.error;
        } else if (serverMessage && !LARAVEL_DEFAULT_MESSAGES.includes(serverMessage)) {
            message = serverMessage;
        } else {
            message = messageByStatus(statusCode);
        }
    } else if (error.request) {
        // Ответа от сервера нет (сеть недоступна, таймаут)
        message = i18n.t('errors.network');
        isNetworkError = true;
    } else {
        // Ошибка до запроса или в коде вокруг него: её текст — для разработчика, не для экрана
        message = i18n.t('errors.generic');
    }

    console.error(`Error (${statusCode}):`, message, error.message ?? '');

    if (errorAction.length <= 1) {
        errorAction(message);
    } else {
        errorAction(message, isNetworkError, statusCode);
    }
}
